// useAppDataLoader.js
import queryKeys from "@configs/react-query/query-key";
import { LOCAL_STORAGE, LOGIN_CONTEXT } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { isLogoutInProgress } from "@auth/keycloakClient";
import { getLocalStorage, setLocalStorage } from "@utils/local-storage";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { clearStore, saveIndexDBData, saveStoredSyncMetadataList, STORES } from "../utils/indexedDb";
import { API } from "@apis/index";
import { useSseSyncReconciler } from "@hooks/useSseSyncReconciler";

const VISIBILITY_RECONCILE_MIN_MS = 60_000;
const INTERVAL_RECONCILE_MS = 15 * 60 * 1000;

export const useAutosyncAllDataAndStore = () => {
  const [isSyncingAllData, setIsSyncingAllData] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [isInitialSync, setIsInitialSync] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const { permissions } = useContext(PermissionContext) || { permissions: [] };

  const hasRunInitialSync = useRef(false);
  const lastVisibilityReconcileAtRef = useRef(0);

  const hasPermissionForResource = useCallback(
    (resource) => {
      if (!resource.requiredPermissions) {
        return true;
      }
      return resource.requiredPermissions.some((permission) => permissions.includes(permission));
    },
    [permissions],
  );

  const resources = useMemo(
    () => [
      {
        key: STORES.PROJECTS,
        url: queryKeys.getProjectList,
        method: "POST",
        body: {},
        idKey: "projectSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.VERIFICATION_REPORTS,
        url: queryKeys.getVerificationReportList,
        method: "POST",
        body: {},
        idKey: "verificationReportSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.NOTICE_INFOS,
        url: queryKeys.getNoticeInfosProject,
        method: "POST",
        body: {},
        idKey: "noticeInfoSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.MERGED_SBOMS,
        url: queryKeys.getSbomMergedList,
        method: "POST",
        body: {},
        idKey: "mergedSbomSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.PREVIEWS,
        url: queryKeys.getProjectPreviews,
        method: "POST",
        body: {},
        idKey: "projectPreviewSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.REQUESTS,
        url: queryKeys.getActionList,
        method: "POST",
        body: {},
        idKey: "actionId",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.IMPORT_COMPONENTS,
        url: queryKeys.getImportComponentsList,
        method: "POST",
        body: {},
        idKey: "importComponentSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.VULNERABILITY_STATUSES,
        url: queryKeys.getVulnerabilityStatus,
        method: "POST",
        body: {},
        idKey: "id",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.BULLETINS,
        url: queryKeys.getAnnouncementList,
        method: "POST",
        body: {},
        idKey: "bulletinSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.QNAS,
        url: queryKeys.getQnaList,
        method: "POST",
        body: {},
        idKey: "qnaSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.FAQS,
        url: queryKeys.getFaqList,
        method: "POST",
        body: {},
        idKey: "faqSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.ARCHIVES,
        url: queryKeys.getArchiveList,
        method: "POST",
        body: {},
        idKey: "archiveSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      },
      {
        key: STORES.USERS,
        url: queryKeys.getUserList,
        method: "POST",
        body: {},
        idKey: "userId",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER],
      },
      {
        key: STORES.SBOMS,
        url: queryKeys.getSbomList,
        method: "POST",
        body: {},
        idKey: "sbomSeq",
        requiredPermissions: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.SBOM_EDIT],
      },
    ],
    [],
  );

  const fetchAndStore = useCallback(async ({ key, url, method, body, idKey }) => {
    try {
      let res = null;
      if (method === "POST") {
        res = await API.post(url, body);
      } else {
        res = await API.get(url);
      }

      if (res.status === 200 && Array.isArray(res.data)) {
        await clearStore(key);
        await saveIndexDBData(key, res.data, idKey);
      } else {
        console.warn(`Invalid response for ${key}:`, res);
      }
    } catch (error) {
      console.error(`Failed to sync "${key}"`, error);
      throw error;
    }
  }, []);

  const refreshStoredSyncMetadata = useCallback(async () => {
    try {
      const response = await API.get(queryKeys.getSyncMetadata);
      const metadataList = Array.isArray(response?.data) ? response.data : [];
      await saveStoredSyncMetadataList(metadataList);
    } catch (error) {
      console.warn("Failed to refresh stored sync metadata", error);
    }
  }, []);

  const dispatchSyncComplete = useCallback((now) => {
    window.lastDataSyncTime = now.getTime();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("dataSyncComplete", {
          detail: { syncTime: now },
        }),
      );
    }, 100);
  }, []);

  const syncAllDataAndStore = useCallback(async () => {
    if (permissions.length === 0 || isLogoutInProgress()) {
      return;
    }
    setIsSyncingAllData(true);
    window.isSyncingAllData = true;
    setIsLoading(true);
    setSyncError(null);

    let now = null;
    try {
      const allowedResources = resources.filter((resource) => hasPermissionForResource(resource));
      const promises = allowedResources.map((resource) => fetchAndStore({ ...resource, idKey: resource.idKey }));
      await Promise.all(promises);
      await refreshStoredSyncMetadata();
      now = new Date();
      setLastSyncTime(now);
      if (!isLogoutInProgress()) {
        setLocalStorage(LOCAL_STORAGE.SYNCED_ALL_DATA, "true");
      }
      if (isInitialSync) {
        setIsInitialSync(false);
      }
    } catch (error) {
      console.error(`Failed to sync all data`, error);
      setSyncError(error);
      now = new Date();
    } finally {
      setIsSyncingAllData(false);
      window.isSyncingAllData = false;
      setIsLoading(false);
      if (now) {
        dispatchSyncComplete(now);
      }
    }
  }, [hasPermissionForResource, resources, fetchAndStore, isInitialSync, permissions.length, dispatchSyncComplete, refreshStoredSyncMetadata]);

  // Does not refresh the stored sync metadata: callers own that so a batch of
  // entities costs one /sync/metadata request instead of one per entity.
  const syncEntityByKey = useCallback(
    async (storeKey) => {
      setIsLoading(true);
      setSyncError(null);

      try {
        const resource = resources.find((resource) => resource.key === storeKey);

        if (resource && hasPermissionForResource(resource)) {
          await fetchAndStore({
            ...resource,
            idKey: resource.idKey,
          });

          const now = new Date();
          setLastSyncTime(now);
          dispatchSyncComplete(now);
        } else {
          console.warn(`${storeKey} resource not found or user lacks permission`);
        }
      } catch (error) {
        setSyncError(error);
        console.error(`Failed to sync ${storeKey}`, error);
      } finally {
        setIsLoading(false);
      }
    },
    [hasPermissionForResource, resources, fetchAndStore, dispatchSyncComplete],
  );

  const { reconcile } = useSseSyncReconciler({
    syncEntityByKey,
    hasPermissionForResource,
    resources,
  });

  // Initial path: full seed if never synced; otherwise sync metadata reconcile.
  useEffect(() => {
    if (hasRunInitialSync.current || permissions.length === 0) {
      return;
    }
    hasRunInitialSync.current = true;
    const hasSynced = getLocalStorage(LOCAL_STORAGE.SYNCED_ALL_DATA);
    if (!hasSynced) {
      syncAllDataAndStore();
    } else {
      reconcile({ force: true });
    }
  }, [permissions.length, syncAllDataAndStore, reconcile]);

  // visibilitychange / online / interval backstop
  useEffect(() => {
    const maybeReconcile = (force = false) => {
      const hasSynced = getLocalStorage(LOCAL_STORAGE.SYNCED_ALL_DATA);
      if (!hasSynced || permissions.length === 0 || isLogoutInProgress()) {
        return;
      }
      reconcile({ force });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastVisibilityReconcileAtRef.current < VISIBILITY_RECONCILE_MIN_MS) {
        return;
      }
      lastVisibilityReconcileAtRef.current = now;
      maybeReconcile(false);
    };

    const onOnline = () => {
      maybeReconcile(true);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        maybeReconcile(false);
      }
    }, INTERVAL_RECONCILE_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
      clearInterval(intervalId);
    };
  }, [permissions.length, reconcile]);

  return {
    isSyncingAllData,
    isLoading,
    isInitialSync,
    lastSyncTime,
    syncError,
    syncAllDataAndStore,
    syncEntityByKey,
    refreshStoredSyncMetadata,
    reconcile,
  };
};
